import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { APP_BAR_BORDER, appBarHeight } from '../AppBar';
import useDashboardCompact from '../../../styles/theme/surfaces/useDashboardCompact';
import useDrawerSize, { MAX_WIDTH, MIN_WIDTH } from './useDrawerSize';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettingsOutlined as RolesIcon,
  KeyOutlined as PermissionsIcon,
  BadgeOutlined as CollaboratorsIcon,
  AccountTreeOutlined as DepartmentsIcon,
  PeopleAltOutlined as LeadsIcon,
  BarChartOutlined as MetricsIcon,
  InputOutlined as LeadOriginsIcon,
  StorefrontOutlined as CustomersIcon,
  StoreOutlined as BusinessesIcon,
  LanOutlined as OrganizationIcon,
  SmartToyOutlined as AiMetricsIcon,
  RouteOutlined as JourneyIcon,
  ViewKanbanOutlined as BoardIcon,
  AdsClickOutlined as CampaignsIcon,
  ContactsOutlined as ContactsIcon,
  InsightsOutlined as MetricsGroupIcon,
  SupportAgentOutlined as ServiceGroupIcon,
  GroupsOutlined as TeamGroupIcon,
  SettingsOutlined as SettingsGroupIcon,
  ShieldOutlined as AdminGroupIcon,
  ExtensionOutlined as IntegrationsIcon,
  ExpandLess,
  ExpandMore,
  KeyboardDoubleArrowLeft as CollapseIcon,
  KeyboardDoubleArrowRight as ExpandIcon,
} from '@mui/icons-material';

const RESIZE_KEY_STEP = 16;

const OPEN_GROUPS_KEY = 'dashboard_nav_open_groups';

// Grupos na ordem de uso: visão de gestor, operação, pessoas, cadastros e,
// por último, o que só a equipe interna vê. Grupo sem item visível some.
const navGroups = [
  {
    id: 'metrics',
    labelKey: 'nav.groups.metrics',
    icon: <MetricsGroupIcon />,
    items: [
      { path: '/metrics', labelKey: 'nav.metricsOverview', icon: <MetricsIcon /> },
      { path: '/metrics/ai', labelKey: 'metrics.ai.title', icon: <AiMetricsIcon /> },
    ],
  },
  {
    id: 'service',
    labelKey: 'nav.groups.service',
    icon: <ServiceGroupIcon />,
    items: [
      { path: '/leads/board', labelKey: 'leads.board.title', icon: <BoardIcon /> },
      { path: '/leads', labelKey: 'nav.leads', icon: <LeadsIcon /> },
      { path: '/contacts', labelKey: 'contacts.title', icon: <ContactsIcon /> },
      { path: '/campaigns', labelKey: 'campaigns.title', icon: <CampaignsIcon /> },
      { path: '/metrics/journey', labelKey: 'metrics.journey.title', icon: <JourneyIcon /> },
    ],
  },
  {
    id: 'team',
    labelKey: 'nav.groups.team',
    icon: <TeamGroupIcon />,
    items: [
      { path: '/collaborators', labelKey: 'collaborators.title', icon: <CollaboratorsIcon /> },
      { path: '/departments', labelKey: 'departments.title', icon: <DepartmentsIcon /> },
      { path: '/roles', labelKey: 'roles.title', icon: <RolesIcon /> },
      { path: '/permissions', labelKey: 'permissions.title', icon: <PermissionsIcon /> },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.groups.settings',
    icon: <SettingsGroupIcon />,
    items: [
      // Both: internal users manage every customer's stores, collaborators see
      // their own customer's (per-record actions come from the API).
      { path: '/businesses', labelKey: 'businesses.title', icon: <BusinessesIcon />, everyone: true },
      // Interno escolhe o cliente, colaborador vê o próprio (escopo da API).
      { path: '/organization', labelKey: 'organization.title', icon: <OrganizationIcon />, everyone: true },
      { path: '/lead-origins', labelKey: 'leadOrigins.title', icon: <LeadOriginsIcon /> },
      { path: '/integrations', labelKey: 'integrations.title', icon: <IntegrationsIcon /> },
    ],
  },
  {
    id: 'admin',
    labelKey: 'nav.groups.admin',
    icon: <AdminGroupIcon />,
    items: [
      // Customer (tenant) onboarding is done by the internal team only.
      { path: '/customers', labelKey: 'customers.title', icon: <CustomersIcon />, internalOnly: true },
    ],
  },
];

// Grupos abertos são só conveniência do navegador: sem storage, abre o da rota.
function readOpenGroups() {
  try {
    const stored = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

function writeOpenGroups(ids) {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(ids));
  } catch {
    // Storage bloqueado: os grupos só não sobrevivem ao reload.
  }
}

const navItemSx = {
  borderRadius: 2,
  // Fundo de selected/hover é o padrão do MUI (derivado do primary).
  '&.Mui-selected': {
    color: 'primary.main',
    '& .MuiListItemIcon-root': { color: 'primary.main' },
  },
};

// Alça na borda direita: arrastar muda a largura, duplo clique volta à padrão
// e as setas ajustam pelo teclado.
function ResizeHandle({ width, label, onResize, onReset, onDraggingChange }) {
  const handlePointerDown = (event) => {
    event.preventDefault();
    const startX = event.clientX;
    const move = (e) => onResize(width + e.clientX - startX);
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      onDraggingChange(false);
    };
    // No body, para o cursor não piscar quando o ponteiro sai da alça.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    onDraggingChange(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') onResize(width - RESIZE_KEY_STEP);
    else if (event.key === 'ArrowRight') onResize(width + RESIZE_KEY_STEP);
    else return;
    event.preventDefault();
  };

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onDoubleClick={onReset}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 8,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 1,
        outline: 'none',
        // Linha fina sobre a borda do drawer, visível só ao passar o mouse ou focar.
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: 2,
          height: '100%',
          bgcolor: 'primary.main',
          opacity: 0,
          transition: (theme) => theme.transitions.create('opacity'),
        },
        '&:hover::after, &:focus-visible::after': { opacity: 1 },
      }}
    />
  );
}

function DashboardDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { collaborator, isInternalOnly } = useAuth();
  const compact = useDashboardCompact();
  const { collapsed, width, expandedWidth, toggleCollapsed, resize, resetWidth } = useDrawerSize(compact);
  const [dragging, setDragging] = useState(false);
  const top = appBarHeight(compact) + APP_BAR_BORDER;
  // An internal user without a customer only gets the internal-only entries.
  const isVisible = (item) => {
    if (item.everyone) return true;
    return item.internalOnly ? collaborator?.is_internal : !isInternalOnly;
  };
  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter(isVisible) }))
    .filter((group) => group.items.length > 0);

  // O item mais específico vence: /metrics/ai marca só a IA, não também /metrics.
  const activePath = visibleGroups
    .flatMap((group) => group.items.map((item) => item.path))
    .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  const isActive = (path) => path === activePath;
  const activeGroupId = visibleGroups.find((group) =>
    group.items.some((item) => item.path === activePath),
  )?.id;

  const [openGroups, setOpenGroups] = useState(readOpenGroups);

  // Navegar para outro grupo (ex.: link de lead noutra página) abre esse
  // grupo, sem fechar os que o usuário deixou abertos.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((ids) => (ids.includes(activeGroupId) ? ids : [...ids, activeGroupId]));
  }, [activeGroupId]);

  useEffect(() => {
    writeOpenGroups(openGroups);
  }, [openGroups]);

  const toggleGroup = (id) => {
    setOpenGroups((ids) => (ids.includes(id) ? ids.filter((g) => g !== id) : [...ids, id]));
  };

  const handleNav = (item) => {
    if (item.external) {
      window.location.href = item.external;
    } else {
      navigate(item.path);
    }
  };

  const renderExpanded = () =>
    visibleGroups.map((group) => {
      const open = openGroups.includes(group.id);
      // Grupo fechado com a rota ativa fica destacado para não perder onde se está.
      const holdsActive = !open && group.id === activeGroupId;
      return (
        <React.Fragment key={group.id}>
          <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
            <ListItemButton
              onClick={() => toggleGroup(group.id)}
              aria-expanded={open}
              sx={{ borderRadius: 2 }}
            >
              <ListItemIcon
                sx={{ minWidth: 36, color: holdsActive ? 'primary.main' : 'text.secondary' }}
              >
                {group.icon}
              </ListItemIcon>
              <ListItemText
                primary={t(group.labelKey)}
                slotProps={{
                  primary: {
                    noWrap: true,
                    color: holdsActive ? 'primary.main' : 'text.primary',
                  },
                }}
              />
              {open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
            </ListItemButton>
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {group.items.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ px: 1, mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNav(item)}
                    selected={isActive(item.path)}
                    sx={{ ...navItemSx, pl: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(item.labelKey)} slotProps={{ primary: { noWrap: true } }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    });

  // Recolhido: só ícones, grupos separados por divisória e rótulo no tooltip.
  const renderCollapsed = () =>
    visibleGroups.map((group, index) => (
      <React.Fragment key={group.id}>
        {index > 0 && <Divider sx={{ mx: 1, mb: 0.5 }} />}
        {group.items.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ px: 1, mb: 0.5 }}>
            <Tooltip title={t(item.labelKey)} placement="right">
              <ListItemButton
                onClick={() => handleNav(item)}
                selected={isActive(item.path)}
                sx={{ ...navItemSx, justifyContent: 'center', px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </React.Fragment>
    ));

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        // Anima recolher/expandir; durante o arraste a largura segue o ponteiro.
        transition: (theme) => (dragging ? 'none' : theme.transitions.create('width')),
        '& .MuiDrawer-paper': {
          width,
          overflowX: 'hidden',
          transition: (theme) => (dragging ? 'none' : theme.transitions.create('width')),
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          top,
          height: `calc(100% - ${top}px)`,
        },
      }}
    >
      <List disablePadding sx={{ pt: 1, flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {collapsed ? renderCollapsed() : renderExpanded()}
      </List>
      <Divider />
      {/* Recolhido, o botão fica centralizado; expandido, alinha com os ícones dos itens. */}
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0 : 2, py: 0.5 }}>
        <Tooltip title={collapsed ? t('nav.expand') : t('nav.collapse')} placement="right">
          <IconButton onClick={toggleCollapsed} sx={{ color: 'text.secondary' }}>
            {collapsed ? <ExpandIcon /> : <CollapseIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      {!collapsed && (
        <ResizeHandle
          width={expandedWidth}
          label={t('nav.resize')}
          onResize={resize}
          onReset={resetWidth}
          onDraggingChange={setDragging}
        />
      )}
    </Drawer>
  );
}

export default DashboardDrawer;
